import type { StudioEffect, StudioStep } from "@loumy/contracts";
import { STUDIO_STEP_ORDER } from "@loumy/contracts";
import type { StudioClip } from "../browser-ops/protocol";
import { effectsToWorkerOps, stackHasChromaKey } from "./effectStack";

// §9.17/D139 — PURE Studio-Logik (Segment-Mathe, Step-Ableitung, Op-Aufbau). KEINE React-/
// Worker-/DOM-Abhängigkeit ⇒ in vitest (Node) testbar. Hier liegen die ±1-kritischen
// Konvertierungen (intern HALB-OFFEN [from,to), UI inklusive) und die SSOT-Spiegel zur
// Server-Preisbildung (deriveSteps ↔ editPricePlan studio) an EINER Stelle (Risiko-Eindämmung).

/** Ein Timeline-Clip, intern HALB-OFFEN [from, to) (Python-Slice-nativ), mit EIGENEM Effekt-Stack
 *  (§9.17: Effekte gelten PRO Clip, nicht global). */
export interface StudioSegment {
  id: string;
  from: number; // inklusive (Frame-Index)
  to: number; // EXKLUSIV
  keep: boolean;
  effects: StudioEffect[]; // clip-eigener Effekt-Stack (Reihenfolge = Wirkung)
}

export type StudioResultKind = "image" | "spritesheet";

/** Ein Segment über die GESAMTE Sequenz (Startzustand nach dem Laden), Effekt-Stack leer. */
export function fullTimeline(totalFrames: number, makeId: (i: number) => string = (i) => `seg-${i}`): StudioSegment[] {
  const n = Math.max(0, Math.trunc(totalFrames));
  return [{ id: makeId(0), from: 0, to: n, keep: true, effects: [] }];
}

/**
 * K=Cut am Playhead: das Segment, das `playhead` enthält (from ≤ p < to), wird bei p in zwei
 * Stücke geteilt. Fällt p exakt auf eine Grenze (p===from), ist es ein No-op (kein leeres Stück).
 */
export function splitAt(segments: StudioSegment[], playhead: number, makeId: (suffix: string) => string): StudioSegment[] {
  const p = Math.trunc(playhead);
  const out: StudioSegment[] = [];
  for (const seg of segments) {
    if (p > seg.from && p < seg.to) {
      // Beide Hälften ERBEN den Effekt-Stack des Clips (eigene Objekt-Kopien ⇒ unabhängig editierbar).
      out.push({ id: makeId(`${seg.id}a`), from: seg.from, to: p, keep: seg.keep, effects: seg.effects.map((e) => ({ ...e })) });
      out.push({ id: makeId(`${seg.id}b`), from: p, to: seg.to, keep: seg.keep, effects: seg.effects.map((e) => ({ ...e })) });
    } else {
      out.push(seg);
    }
  }
  return out;
}

/** keep-Flag eines Segments setzen (Löschen/Behalten-Toggle). */
export function setKeep(segments: StudioSegment[], id: string, keep: boolean): StudioSegment[] {
  return segments.map((s) => (s.id === id ? { ...s, keep } : s));
}

/**
 * Clip-Grenzen [from, to) setzen (Trimmen der Clip-Ränder im NLE-Sinn). Klemmt halb-offen
 * gegen [0, totalFrames) und erzwingt mindestens EINEN Frame (from < to). Trimmen berührt
 * NUR diesen Clip — Lücken zu Nachbarn sind erlaubt (gelöschte Frames erscheinen nicht im
 * Output; keptRanges überspringt sie ohnehin). Das ist „ripple"-frei: andere Clips bleiben fix.
 */
export function setRange(segments: StudioSegment[], id: string, from: number, to: number, totalFrames: number): StudioSegment[] {
  const n = Math.max(0, Math.trunc(totalFrames));
  if (n === 0) return segments;
  return segments.map((s) => {
    if (s.id !== id) return s;
    const a = Math.max(0, Math.min(Math.trunc(from), n - 1));
    const b = Math.max(a + 1, Math.min(Math.trunc(to), n));
    return { ...s, from: a, to: b };
  });
}

/** Segment im Array verschieben (DnD-Umordnen). Out-of-range-Indizes werden geklemmt. */
export function reorder(segments: StudioSegment[], fromIndex: number, toIndex: number): StudioSegment[] {
  const n = segments.length;
  const a = Math.max(0, Math.min(Math.trunc(fromIndex), n - 1));
  const b = Math.max(0, Math.min(Math.trunc(toIndex), n - 1));
  if (a === b) return segments;
  const next = segments.slice();
  const [moved] = next.splice(a, 1);
  if (!moved) return segments;
  next.splice(b, 0, moved);
  return next;
}

/** Behaltene Segment-Bereiche (geklemmt, halb-offen, in Listen-Reihenfolge = Output-Reihenfolge). */
export function keptRanges(segments: StudioSegment[], totalFrames: number): [number, number][] {
  const n = Math.max(0, Math.trunc(totalFrames));
  const out: [number, number][] = [];
  for (const seg of segments) {
    if (!seg.keep) continue;
    const a = Math.max(0, Math.min(Math.trunc(seg.from), n));
    const b = Math.max(0, Math.min(Math.trunc(seg.to), n));
    if (b > a) out.push([a, b]);
  }
  return out;
}

/** Output-Frame-Indizes (für Vorschau-Playback im Main-Thread): kept Ranges expandiert. */
export function resolveOutputIndices(segments: StudioSegment[], totalFrames: number): number[] {
  const out: number[] = [];
  for (const [a, b] of keptRanges(segments, totalFrames)) {
    for (let i = a; i < b; i++) out.push(i);
  }
  return out;
}

/** Anzahl der Output-Frames. */
export function outputFrameCount(segments: StudioSegment[], totalFrames: number): number {
  return keptRanges(segments, totalFrames).reduce((acc, [a, b]) => acc + (b - a), 0);
}

/**
 * Identitäts-Sequenz? (genau EIN behaltenes Segment [0, N) in Reihenfolge ⇒ kein Schnitt).
 * Bestimmt, ob die `timeline-cut`-Capability anfällt UND ob der Worker eine segments-Op braucht.
 */
export function isFullPassthrough(segments: StudioSegment[], totalFrames: number): boolean {
  const kept = keptRanges(segments, totalFrames);
  return kept.length === 1 && kept[0]![0] === 0 && kept[0]![1] === Math.max(0, Math.trunc(totalFrames));
}

export interface StudioPlanInput {
  totalFrames: number;
  segments: StudioSegment[];
  resultKind: StudioResultKind;
  /** Komponierter Effekt-Stack (§9.17) — chroma-key liegt jetzt als Effekt hier drin (nicht mehr
   *  als fester Schalter); seine Präsenz speist die Preis-Steps (chroma-key + transparency-crop). */
  effects: StudioEffect[];
  /** Loop-Naht glätten (RIFE) — nur bei Sheet-Output sinnvoll (D139-Entscheidung 5). */
  seam: boolean;
}

/**
 * Studio-Save-Schritte in KANONISCHER Reihenfolge (STUDIO_STEP_ORDER) — SSOT-Spiegel von
 * `editPricePlan` studio (apps/shared). Preis = Σ flat-Steps (s. estimateStudioEditCredits).
 * Bei resultKind='image' zählt das FRAME-EXTRAHIEREN aus einer >1-Frame-Sequenz als Schnitt.
 * Die REINEN Pixel-Effekte (speed/color/transform/outline/glow/pixelate/shadow) sind preis-NEUTRAL
 * und tauchen NICHT in den Steps auf — nur ein aktiver `chroma-key`-Effekt stiftet Preis-Steps.
 */
export function deriveSteps(input: StudioPlanInput): StudioStep[] {
  const { totalFrames, segments, resultKind, effects, seam } = input;
  const steps: StudioStep[] = [];
  const cut =
    resultKind === "image"
      ? Math.max(0, Math.trunc(totalFrames)) > 1 || !isFullPassthrough(segments, totalFrames)
      : !isFullPassthrough(segments, totalFrames);
  if (cut) steps.push("timeline-cut");
  if (seam && resultKind === "spritesheet") steps.push("loop");
  if (stackHasChromaKey(effects)) {
    steps.push("chroma-key");
    steps.push("transparency-crop");
  }
  if (resultKind === "spritesheet") steps.push("spritesheet-pack");
  // Codex-P1: Materialisierungs-Floor. Ein image-Save aus EINEM reinen Pixel-Effekt (z.B. Outline/
  // Pixelate auf einem Einzelbild) hätte sonst 0 Steps ⇒ nicht speicherbar (EditRequest lehnt leere
  // Steps ab). Ein materialisierter Einzelframe-Edit kostet `timeline-cut` (1 cr, = Einzelframe-Preis
  // der Matrix). Sheets tragen immer `spritesheet-pack`; ein Save OHNE jede Änderung/Effekt bleibt
  // leer (No-op, korrekt nicht speicherbar).
  if (steps.length === 0 && resultKind === "image" && effects.some((e) => e.enabled)) {
    steps.push("timeline-cut");
  }
  // Defensive Selbstprüfung: kanonische Reihenfolge erzwingen (sonst lehnt der EditRequest-
  // superRefine den Save ab) — die Konstruktion oben hält sie bereits ein.
  return steps.filter((s, i) => steps.indexOf(s) === i && STUDIO_STEP_ORDER.includes(s));
}

/** @deprecated Aliase auf StudioPlanInput (key/tolerance leben jetzt im chroma-key-Effekt). */
export type StudioOpsInput = StudioPlanInput;

/**
 * Effekte über die BEHALTENEN Clips (flach) — für Preis-Steps (`deriveSteps` via stackHasChromaKey),
 * `activeKeyParams`, WebM-Sperre und Provenance. NUR behaltene Clips (gelöschte Clips erscheinen
 * nicht im Output ⇒ dürfen weder Preis noch Provenance beeinflussen). Reihenfolge = Clip-Reihenfolge,
 * dann Stack-Reihenfolge.
 */
export function allEffects(segments: StudioSegment[]): StudioEffect[] {
  return segments.filter((s) => s.keep).flatMap((s) => s.effects);
}

/**
 * §9.17 — Timeline → Worker-CLIP-Liste (Output-Reihenfolge): je BEHALTENEM Clip seine Quell-Range
 * + sein EIGENER Effekt-Stack (`effectsToWorkerOps`). Effekte gelten so PRO Clip. Die globale
 * RIFE-Naht ist KEINE Clip-Op mehr (sie läuft am st-apply-Request, s. client/worker). `withEffects:
 * false` liefert die ROHEN Clips (leere ops) — z.B. zur Naht-Randframe-Extraktion (vor Effekten).
 * Leere/gelöschte Auswahl ⇒ leere Liste (der Aufrufer verhindert den Save bei leerem Output).
 */
export function buildClipGroups(
  segments: StudioSegment[],
  totalFrames: number,
  opts: { withEffects: boolean },
): StudioClip[] {
  const n = Math.max(0, Math.trunc(totalFrames));
  const clips: StudioClip[] = [];
  for (const seg of segments) {
    if (!seg.keep) continue;
    const a = Math.max(0, Math.min(Math.trunc(seg.from), n));
    const b = Math.max(0, Math.min(Math.trunc(seg.to), n));
    if (b <= a) continue;
    clips.push({ segments: [[a, b]], ops: opts.withEffects ? effectsToWorkerOps(seg.effects) : [] });
  }
  return clips;
}
